import _ from 'lodash'
import type { Component } from '@wix/thunderbolt-becky-types'
import { MASTER_PAGE_ID, SingleLayoutData } from '@wix/thunderbolt-becky-types'
import type {
	ZIndexIdToIndex,
	ZIndexCss,
	ZIndexPageChildren as ZIndexOriginalChildren,
	ZIndexCssChild,
	ZIndexCssPage,
} from './zIndexCss.types'
import { sortMasterPageChildrenIntoGroups } from '@wix/thunderbolt-becky-root'
import { isComponentFixed } from './zIndexCompNodeHelpers'
import {
	ABOVE_ALL_COMPONENTS_CLASSIC,
	ABOVE_ALL_COMP_Z_INDEX,
	ABOVE_ALL_IN_CONTAINER_Z_INDEX_DEFINED,
	ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED,
	CONTAINER_IDS,
	CSS_PROPERTIES,
	EXCLUDED_COMPONENTS_CLASSIC,
	INITIAL_Z_INDEX,
	PINNED_LAYER_SUFFIX,
	Z_INDEX_RANGE,
} from './constants'

const isAboveAllComponents = (compId: string) => ABOVE_ALL_COMPONENTS_CLASSIC.includes(compId)

const getPageZindexValues = (
	comp: Component,
	pagePinnedChildren: Array<Component>,
	pageAboveAllComponentsChildren: Array<Component>,
	pinnedComponentToStackingOrder: ZIndexIdToIndex,
	isPopup: boolean
): ZIndexCssPage => {
	const pageSelector = isPopup ? comp.id : MASTER_PAGE_ID

	const zIndexPinnedChildren = pagePinnedChildren.reduce((acc: ZIndexCssPage, childComp) => {
		acc[`${childComp.id}${PINNED_LAYER_SUFFIX}`] = {
			[CSS_PROPERTIES.Z_INDEX]: `calc(${CSS_PROPERTIES.PINNED_LAYER_IN_CONTAINER_VAR} + ${
				pinnedComponentToStackingOrder[childComp.id]
			})`,
		}
		return acc
	}, {})

	const zIndexAboveAllComponentsChildren = pageAboveAllComponentsChildren.reduce((acc: ZIndexCssPage, childComp) => {
		acc[childComp.id] = {
			[CSS_PROPERTIES.Z_INDEX]: ABOVE_ALL_COMP_Z_INDEX,
			[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_DEFINED,
		}
		return acc
	}, {})

	const zIndexPage = {
		[pageSelector]: isPopup
			? {
				[CSS_PROPERTIES.PINNED_LAYER_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED + 1,
				[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED,
			}
			: { [CSS_PROPERTIES.PINNED_LAYERS_IN_PAGE]: pagePinnedChildren.length },
	}

	return _.assign(zIndexPinnedChildren, zIndexAboveAllComponentsChildren, zIndexPage)
}

// calc comp z-index value based on its index
const getZindexDefaultValue: (index: number) => number = (index) => index * Z_INDEX_RANGE + INITIAL_Z_INDEX

const getZindexCalculatedValue = (
	compId: string,
	siteHeaderIndex: number,
	pagesContainerIndex: number,
	excludedComponents: Array<string>,
	isCompFixed: boolean,
	componentStackingOrder: number,
	zIndexDefaultValue: number
): string | number | null => {
	// In case comp is above all components, return the above all z-index value
	if (isAboveAllComponents(compId)) {
		return ABOVE_ALL_COMP_Z_INDEX
	}
	// In case comp is site header and it's above the pages container index and not fixed we return the above all in container z-index not defined value
	if (compId === CONTAINER_IDS.SITE_HEADER && siteHeaderIndex > pagesContainerIndex && !isCompFixed) {
		return ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED
	}
	// comp is not site_header, pages_container, site_footer or SOAP
	// or comp is site_header/footer and is fixed
	if (
		!excludedComponents.includes(compId) ||
		((compId === CONTAINER_IDS.SITE_HEADER || compId === CONTAINER_IDS.SITE_FOOTER) && isCompFixed)
	) {
		// for components above page_container, add the number of pinned layers in page
		if (componentStackingOrder > pagesContainerIndex) {
			return `calc(${CSS_PROPERTIES.PINNED_LAYERS_IN_PAGE_VAR} + ${zIndexDefaultValue})`
		} else {
			// Return the default z-index value since we are fixed position and should be above the pages container
			return zIndexDefaultValue
		}
	}

	return null
}

const getMasterPageChildZindexValue = (
	masterPageZIndexChild: ZIndexCssChild,
	siteHeaderIndex: number,
	pagesContainerIndex: number,
	excludedComponents: Array<string>,
	componentsToStackingOrder: ZIndexIdToIndex
): ZIndexCss => {
	const masterPageChildComp = masterPageZIndexChild.component
	const compId = masterPageChildComp.id
	const isCompFixed = isComponentFixed(masterPageZIndexChild)
	const componentStackingOrder = componentsToStackingOrder[compId]
	const zIndexDefaultValue = getZindexDefaultValue(componentStackingOrder)

	const zIndex: string | number | null = getZindexCalculatedValue(
		compId,
		siteHeaderIndex,
		pagesContainerIndex,
		excludedComponents,
		isCompFixed,
		componentStackingOrder,
		zIndexDefaultValue
	)

	if (zIndex) {
		return {
			[CSS_PROPERTIES.Z_INDEX]: zIndex,
			[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_DEFINED, // if container have z-index, it forms a stacking context so we set a high number
		}
	}
	return {
		[CSS_PROPERTIES.PINNED_LAYER_IN_CONTAINER]: zIndexDefaultValue,
		[CSS_PROPERTIES.ABOVE_ALL_IN_CONTAINER]: ABOVE_ALL_IN_CONTAINER_Z_INDEX_NOT_DEFINED,
	}
}

const getMasterPageZindexValues = (
	pageOriginalChildren: ZIndexOriginalChildren = [],
	pinnedComponentToStackingOrder: { [key: string]: number },
	isMobileView: boolean
): ZIndexCssPage => {
	const componentsToStackingOrder: ZIndexIdToIndex = {}
	const pageOriginalChildrenIds: Array<string> = []
	const pageOriginalChildrenLayout: { [compId: string]: Component['layout'] } = {}
	pageOriginalChildren.forEach((zIndexChild, index) => {
		componentsToStackingOrder[zIndexChild?.component.id] = index
		pageOriginalChildrenIds.push(zIndexChild?.component.id)
		pageOriginalChildrenLayout[zIndexChild?.component.id] = zIndexChild?.component.layout
	})

	const siteHeaderIndex = componentsToStackingOrder.SITE_HEADER
	const pagesContainerIndex = componentsToStackingOrder.PAGES_CONTAINER

	const masterPageGroups = sortMasterPageChildrenIntoGroups(
		pageOriginalChildrenIds,
		pageOriginalChildrenLayout,
		isMobileView
	)
	// Concating the soap components before and after
	const soapComponentsIds = _.concat(masterPageGroups.soap.beforeMain, masterPageGroups.soap.afterMain)

	// SOAP components should not have z-index
	const excludedComponents = EXCLUDED_COMPONENTS_CLASSIC.concat(soapComponentsIds)
	const masterPageZIndexResult: ZIndexCssPage = pageOriginalChildren.reduce(
		(acc: ZIndexCssPage, zIndexChild: ZIndexCssChild) => {
			const comp = zIndexChild?.component
			const pinnedLayerId = `${comp.id}${PINNED_LAYER_SUFFIX}`
			const compId = typeof pinnedComponentToStackingOrder[comp.id] !== 'undefined' ? pinnedLayerId : comp.id
			acc[compId] = getMasterPageChildZindexValue(
				zIndexChild,
				siteHeaderIndex,
				pagesContainerIndex,
				excludedComponents,
				componentsToStackingOrder
			)
			return acc
		},
		{}
	)

	return masterPageZIndexResult
}

export const getClassicZIndexValues = (
	component: Component,
	pageOriginalChildren: ZIndexOriginalChildren = [],
	isMobileView: boolean,
	isPopup: boolean
): ZIndexCssPage => {
	const compId = component.id

	const initialAccumulator: {
		pagePinnedChildren: Array<Component>
		pageAboveAllComponentsChildren: Array<Component>
		pinnedComponentToStackingOrder: ZIndexIdToIndex
	} = {
		pagePinnedChildren: [],
		pageAboveAllComponentsChildren: [],
		pinnedComponentToStackingOrder: {},
	}
	const {
		pagePinnedChildren,
		pageAboveAllComponentsChildren,
		pinnedComponentToStackingOrder,
	} = pageOriginalChildren.reduce((acc, child) => {
		if (child.pinnedStyle) {
			const pinnedComponent = child.component
			acc.pagePinnedChildren.push(pinnedComponent)
			acc.pinnedComponentToStackingOrder[pinnedComponent.id] = acc.pagePinnedChildren.length - 1
		}

		if (child.component && isAboveAllComponents(child.component.id)) {
			acc.pageAboveAllComponentsChildren.push(child.component)
		}
		return acc
	}, initialAccumulator)

	if (compId === CONTAINER_IDS.MASTER_PAGE) {
		// Handle Master Page rendering
		return getMasterPageZindexValues(pageOriginalChildren, pinnedComponentToStackingOrder, isMobileView)
	} else {
		// Handle Page rendering
		return getPageZindexValues(
			component,
			pagePinnedChildren,
			pageAboveAllComponentsChildren,
			pinnedComponentToStackingOrder,
			isPopup
		)
	}
}
